import React, { useState, useCallback } from 'react';
import { CustomDropdown } from '../../components';


const SampleDropdownUsage = () => {
  // Sample options data
  const fruitOptions = [
    { value: 'apple', label: 'Apple' },
    { value: 'banana', label: 'Banana' },
    { value: 'cherry', label: 'Cherry' },
    { value: 'date', label: 'Date' },
    { value: 'elderberry', label: 'Elderberry' },
    { value: 'fig', label: 'Fig' },
    { value: 'grape', label: 'Grape' },
    { value: 'honeydew', label: 'Honeydew' },
  ];

  const [countryOptions, setCountryOptions] = useState([
    { value: 'us', label: 'United States' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'ca', label: 'Canada' },
    { value: 'au', label: 'Australia' },
    { value: 'de', label: 'Germany' },
    { value: 'fr', label: 'France' },
    { value: 'jp', label: 'Japan' },
    { value: 'in', label: 'India' },
  ]);

  const categoryOptions = [
    { value: 'electronics', label: 'Electronics' },
    { value: 'clothing', label: 'Clothing' },
    { value: 'books', label: 'Books' },
    { value: 'home', label: 'Home & Garden' },
    { value: 'sports', label: 'Sports & Outdoors' },
  ];

  // State for different dropdown examples
  const [singleSelect, setSingleSelect] = useState(null);
  const [multiSelect, setMultiSelect] = useState([]);
  const [searchableSelect, setSearchableSelect] = useState(null);
  const [creatableSelect, setCreatableSelect] = useState(null);
  const [creatableMultiSelect, setCreatableMultiSelect] = useState([]);
  const [disabledSelect, setDisabledSelect] = useState({ value: 'apple', label: 'Apple' });
  const [errorSelect, setErrorSelect] = useState(null);
  const [smallSelect, setSmallSelect] = useState(null);
  const [largeSelect, setLargeSelect] = useState(null);
  const [loadingSelect, setLoadingSelect] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Handler for creating new options
  const handleCreateOption = useCallback((inputValue) => {
    const newOption = {
      value: inputValue.toLowerCase().replace(/\s+/g, '-'),
      label: inputValue,
    };
    setCountryOptions((prev) => [...prev, newOption]);
    setCreatableSelect(newOption);
  }, []);

  const handleCreateMultiOption = useCallback((inputValue) => {
    const newOption = {
      value: inputValue.toLowerCase().replace(/\s+/g, '-'),
      label: inputValue,
    };
    setCountryOptions((prev) => [...prev, newOption]);
    setCreatableMultiSelect((prev) => [...prev, newOption]);
  }, []);

  // Simulate loading
  const handleLoadingDemo = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 2000);
  };

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
      <h1 style={{ marginBottom: '24px', color: '#1f2937' }}>
        CustomDropdown Usage Examples
      </h1>

      {/* Basic Single Select */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          1. Basic Single Select
        </h2>
        <CustomDropdown
          label="Select a Fruit"
          name="singleFruit"
          value={singleSelect}
          onChange={setSingleSelect}
          options={fruitOptions}
          placeholder="Choose a fruit..."
          helperText="Select your favorite fruit from the list"
        />
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '8px' }}>
          Selected: {singleSelect ? singleSelect.label : 'None'}
        </p>
      </section>

      {/* Multi Select */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          2. Multi Select
        </h2>
        <CustomDropdown
          label="Select Multiple Fruits"
          name="multiFruit"
          value={multiSelect}
          onChange={setMultiSelect}
          options={fruitOptions}
          placeholder="Choose multiple fruits..."
          isMulti
          helperText="You can select multiple options"
        />
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '8px' }}>
          Selected: {multiSelect.length > 0 ? multiSelect.map((o) => o.label).join(', ') : 'None'}
        </p>
      </section>

      {/* Searchable Select */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          3. Searchable Select (with search bar)
        </h2>
        <CustomDropdown
          label="Search and Select Country"
          name="searchCountry"
          value={searchableSelect}
          onChange={setSearchableSelect}
          options={countryOptions}
          placeholder="Type to search..."
          isSearchable
          helperText="Start typing to filter options"
        />
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '8px' }}>
          Selected: {searchableSelect ? searchableSelect.label : 'None'}
        </p>
      </section>

      {/* Creatable Single Select */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          4. Creatable Single Select (type "create" to add new)
        </h2>
        <CustomDropdown
          label="Select or Create Country"
          name="creatableCountry"
          value={creatableSelect}
          onChange={setCreatableSelect}
          options={countryOptions}
          placeholder="Select or type to create..."
          isSearchable
          createOptionKey="create"
          onCreateOption={handleCreateOption}
          createOptionLabel={(input) => `+ Create "${input}"`}
          helperText="Type a new country name and press Enter to create it"
        />
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '8px' }}>
          Selected: {creatableSelect ? creatableSelect.label : 'None'}
        </p>
      </section>

      {/* Creatable Multi Select */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          5. Creatable Multi Select
        </h2>
        <CustomDropdown
          label="Select or Create Multiple Countries"
          name="creatableMultiCountry"
          value={creatableMultiSelect}
          onChange={setCreatableMultiSelect}
          options={countryOptions}
          placeholder="Select or create countries..."
          isMulti
          isSearchable
          createOptionKey="create"
          onCreateOption={handleCreateMultiOption}
          createOptionLabel={(input) => `+ Add "${input}"`}
          helperText="Select existing or create new countries"
        />
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '8px' }}>
          Selected: {creatableMultiSelect.length > 0 ? creatableMultiSelect.map((o) => o.label).join(', ') : 'None'}
        </p>
      </section>

      {/* Required Field with Error */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          6. Required Field with Error State
        </h2>
        <CustomDropdown
          label="Required Category"
          name="requiredCategory"
          value={errorSelect}
          onChange={setErrorSelect}
          options={categoryOptions}
          placeholder="Select a category..."
          required
          error={!errorSelect ? 'This field is required' : ''}
        />
      </section>

      {/* Disabled Select */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          7. Disabled Select
        </h2>
        <CustomDropdown
          label="Disabled Dropdown"
          name="disabledFruit"
          value={disabledSelect}
          onChange={setDisabledSelect}
          options={fruitOptions}
          disabled
          helperText="This dropdown is disabled"
        />
      </section>

      {/* Size Variants */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          8. Size Variants
        </h2>

        <div style={{ marginBottom: '16px' }}>
          <CustomDropdown
            label="Small Size"
            name="smallSize"
            value={smallSelect}
            onChange={setSmallSelect}
            options={fruitOptions}
            placeholder="Small dropdown"
            size="sm"
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <CustomDropdown
            label="Medium Size (Default)"
            name="mediumSize"
            value={singleSelect}
            onChange={setSingleSelect}
            options={fruitOptions}
            placeholder="Medium dropdown"
            size="md"
          />
        </div>

        <CustomDropdown
          label="Large Size"
          name="largeSize"
          value={largeSelect}
          onChange={setLargeSelect}
          options={fruitOptions}
          placeholder="Large dropdown"
          size="lg"
        />
      </section>

      {/* Loading State */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          9. Loading State
        </h2>
        <button
          onClick={handleLoadingDemo}
          style={{
            marginBottom: '12px',
            padding: '8px 16px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Simulate Loading (2s)
        </button>
        <CustomDropdown
          label="Loading Demo"
          name="loadingDemo"
          value={loadingSelect}
          onChange={setLoadingSelect}
          options={fruitOptions}
          placeholder="Click button above to see loading..."
          isLoading={isLoading}
          loadingMessage={() => 'Fetching options...'}
        />
      </section>

      {/* Non-Searchable Select */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          10. Non-Searchable Select (no search bar)
        </h2>
        <CustomDropdown
          label="Simple Select without Search"
          name="noSearchSelect"
          value={singleSelect}
          onChange={setSingleSelect}
          options={fruitOptions}
          placeholder="Select without search..."
          isSearchable={false}
          helperText="This dropdown has no search functionality"
        />
      </section>

      {/* Non-Clearable Select */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          11. Non-Clearable Select
        </h2>
        <CustomDropdown
          label="Cannot Clear Selection"
          name="noClearSelect"
          value={singleSelect || fruitOptions[0]}
          onChange={setSingleSelect}
          options={fruitOptions}
          isClearable={false}
          helperText="Once selected, you cannot clear this dropdown"
        />
      </section>

      {/* Hide Selected Options */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          12. Multi Select with Hidden Selected Options
        </h2>
        <CustomDropdown
          label="Selected Options Hidden from List"
          name="hideSelectedMulti"
          value={multiSelect}
          onChange={setMultiSelect}
          options={fruitOptions}
          placeholder="Selected items disappear from list..."
          isMulti
          hideSelectedOptions
          helperText="Selected options are removed from the dropdown list"
        />
      </section>

      {/* Menu Placement Top */}
      <section style={{ marginBottom: '32px', paddingTop: '200px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>
          13. Menu Placement Top
        </h2>
        <CustomDropdown
          label="Opens Upward"
          name="menuTopSelect"
          value={singleSelect}
          onChange={setSingleSelect}
          options={fruitOptions}
          placeholder="Menu opens above..."
          menuPlacement="top"
        />
      </section>
    </div>
  );
};

export default SampleDropdownUsage;
